/* AgentMeeting — shared mock data: agents, transcripts, models */

export type ModelId =
  | 'openai-gpt4o-mini'
  | 'openai-gpt4o'
  | 'openai-o1'
  | 'claude-sonnet'
  | 'claude-haiku'
  | 'claude-opus'
  | 'gemini-flash'
  | 'gemini-pro'
  | 'deepseek-chat'
  | 'deepseek-reasoner';

export interface ModelInfo {
  name: string;
  short: string;
  vendor: string;
}

export const MODELS: Record<ModelId, ModelInfo> = {
  'openai-gpt4o-mini': { name: 'openai/gpt-4o-mini',          short: 'gpt-4o-mini', vendor: 'OpenAI' },
  'openai-gpt4o':      { name: 'openai/gpt-4o',               short: 'gpt-4o',      vendor: 'OpenAI' },
  'openai-o1':         { name: 'openai/o1-mini',              short: 'o1-mini',     vendor: 'OpenAI' },
  'claude-sonnet':     { name: 'anthropic/claude-3-5-sonnet', short: 'sonnet-3.5',  vendor: 'Anthropic' },
  'claude-haiku':      { name: 'anthropic/claude-3-5-haiku',  short: 'haiku-3.5',   vendor: 'Anthropic' },
  'claude-opus':       { name: 'anthropic/claude-3-opus',     short: 'opus-3',      vendor: 'Anthropic' },
  'gemini-flash':      { name: 'google/gemini-2.0-flash',     short: 'gem-flash',   vendor: 'Google' },
  'gemini-pro':        { name: 'google/gemini-1.5-pro',       short: 'gem-pro',     vendor: 'Google' },
  'deepseek-chat':     { name: 'deepseek/deepseek-chat',      short: 'ds-chat',     vendor: 'DeepSeek' },
  'deepseek-reasoner': { name: 'deepseek/deepseek-reasoner',  short: 'ds-r',        vendor: 'DeepSeek' },
};

export function ModelGlyph({ id, size = 10 }: { id: ModelId; size?: number }) {
  const s = size;
  if (typeof id === 'string' && id.startsWith('claude-')) return (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <path d="M3 12L6 4h1.4l3 8H9.1L8.4 10H5.6L4.9 12H3zm2.9-3.3h2L6.9 6.2 5.9 8.7zM9 12l3-8h1.5l3 8H15l-.7-2h-2.8L10.8 12H9zm2.9-3.3h2l-1-2.5-1 2.5z" fill="currentColor" />
    </svg>
  );
  if (typeof id === 'string' && id.startsWith('openai-')) return (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M8 4v8M4 8h8" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
  if (typeof id === 'string' && id.startsWith('gemini-')) return (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <path d="M8 1L9.5 6.5L15 8L9.5 9.5L8 15L6.5 9.5L1 8L6.5 6.5z" fill="currentColor" />
    </svg>
  );
  if (typeof id === 'string' && id.startsWith('deepseek-')) return (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <path d="M8 1 L15 8 L8 15 L1 8 z" fill="currentColor" />
    </svg>
  );
  return (
    <svg width={s} height={s} viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.2" fill="none" />
    </svg>
  );
}

export type Stance = 'pro' | 'con' | 'neutral' | 'finance' | 'risk' | 'judge' | 'host' | 'user';

export const STANCE_COLOR: Record<Stance, string> = {
  pro:     'var(--pro)',
  con:     'var(--err)',
  neutral: 'var(--neutral)',
  finance: 'var(--finance)',
  risk:    'var(--warn)',
  judge:   'var(--judge)',
  host:    'var(--live)',
  user:    'var(--fg-0)',
};

export interface AgentT {
  id: string;
  name: string;
  short: string;
  stance: Stance;
  stanceLabel: string;
  model: ModelId | null;
  persona: string;
}

export const AGENTS: AgentT[] = [
  { id: 'A1', name: 'Aria · CEO',       short: 'AC', stance: 'pro',     stanceLabel: '推进派',  model: 'claude-sonnet',     persona: '战略乐观、增长导向,看长期上行。' },
  { id: 'A2', name: 'Bran · CFO',       short: 'BC', stance: 'finance', stanceLabel: '财务派',  model: 'openai-gpt4o',      persona: '看现金流、单位经济、回收期。' },
  { id: 'A3', name: 'Cyra · 风险官',    short: 'CR', stance: 'risk',    stanceLabel: '风险派',  model: 'gemini-pro',        persona: '识别黑天鹅与监管风险。' },
  { id: 'A4', name: 'Doru · 反方',      short: 'DR', stance: 'con',     stanceLabel: '反对派',  model: 'claude-sonnet',     persona: '红队角色,寻找方案漏洞。' },
  { id: 'A5', name: 'Echo · 用户代表',  short: 'EU', stance: 'neutral', stanceLabel: '用户视角', model: 'openai-gpt4o-mini', persona: '从普通消费者体验角度评估。' },
  { id: 'A6', name: 'Faye · 评委',      short: 'FJ', stance: 'judge',   stanceLabel: '评委',     model: 'claude-sonnet',     persona: '裁定胜负,只在 closing 发言。' },
  { id: 'U1', name: '你 · Director',    short: 'YO', stance: 'user',    stanceLabel: '导演',     model: null,                persona: '人类导演位。' },
];

export interface MessageT {
  id: string;
  round: number;
  agentId: string;
  kind: 'speech' | 'vote' | 'system' | 'aside';
  tokensIn: number;
  tokensOut: number;
  costCents: number;
  parent: string | null;
  ts: string;
  chunks: string[];
  streaming?: boolean;
  isUser?: boolean;
  role?: string;
}

export const TRANSCRIPT: MessageT[] = [
  {
    id: 'm-001', round: 0, agentId: 'A1', kind: 'speech', tokensIn: 1240, tokensOut: 187, costCents: 4,
    parent: null, ts: '14:02:11',
    chunks: [
      '我先开门见山:', ' 我们应该立刻进入巴西市场。', ' 拉美 SaaS 渗透率仍在 ',
      '低位,', ' 圣保罗一个城市的中型企业数量就超过我们目前两个州的总和。',
      ' 等再等半年, 本地玩家会先把品牌占住。',
    ],
  },
  {
    id: 'm-002', round: 0, agentId: 'A2', kind: 'speech', tokensIn: 1402, tokensOut: 213, costCents: 5,
    parent: 'm-001', ts: '14:02:54',
    chunks: [
      '@Aria 我理解紧迫感, 但数字上我不同意。', ' 巴西的获客成本在过去 ',
      '12 个月翻了 1.8x,', ' 而 ARPU 比北美低 42%。', ' 现金回收期会从 9 个月拉到 ',
      '~22 个月。', ' 在我们当前 burn 下, 这意味着要再融一轮才能跑得通。',
    ],
  },
  {
    id: 'm-003', round: 0, agentId: 'A3', kind: 'speech', tokensIn: 1588, tokensOut: 156, costCents: 4,
    parent: 'm-002', ts: '14:03:30',
    chunks: [
      '风险侧补一点:', ' LGPD 数据合规和我们当前架构差距不小,', ' 至少 8 周工程量。',
      ' 加上汇率, 前 18 个月的 P&L 会非常难看。',
    ],
  },
  {
    id: 'm-004', round: 1, agentId: 'A4', kind: 'speech', tokensIn: 1740, tokensOut: 198, costCents: 5,
    parent: null, ts: '14:04:18',
    chunks: [
      '反方陈述。', ' 三个反对理由:', ' (1) 时机错位 — 巴西利率周期还在高位;',
      ' (2) 渠道错位 — 我们没有当地销售;',
      ' (3) 注意力错位 — 北美客户流失率刚抬头, 这时候出海等于左右开弓。',
    ],
  },
  {
    id: 'm-005', round: 1, agentId: 'A5', kind: 'speech', tokensIn: 1810, tokensOut: 142, costCents: 3,
    parent: 'm-004', ts: '14:04:55',
    chunks: [
      '从用户视角讲一句:', ' 巴西中小企业对葡语本地化要求很高,',
      ' 翻译过去的产品体验差距用户能立刻察觉。', ' 这点常常被低估。',
    ],
  },
  {
    id: 'm-006', round: 2, agentId: 'A1', kind: 'speech', tokensIn: 2104, tokensOut: 220, costCents: 6,
    parent: 'm-002', ts: '14:05:42',
    chunks: [
      '回应 @Bran:', ' 我承认 22 个月回收期太长。',
      ' 修正提议: 不直接进, 而是 9 个月 pilot —',
      ' 只做圣保罗 + 葡语 MVP + 一个本地合作伙伴渠道。',
      ' 烧 $1.2M 上限, 到期不及预期就撤。',
    ],
  },
  {
    id: 'm-007', round: 2, agentId: 'A2', kind: 'speech', streaming: true, tokensIn: 2320, tokensOut: 0, costCents: 0,
    parent: 'm-006', ts: '14:06:30',
    chunks: [
      '这个修正可以接受。',
      ' $1.2M 上限对我来说底线是: 第 4 个月看新签 ARR ≥ ',
      '$80k 且 CAC payback < 14 个月,', ' 否则强制 sunset。',
      ' 另外希望把 LGPD 合规预算单独切出来, 不计入 pilot 预算。',
    ],
  },
];

export interface AgendaT {
  round: number;
  label: string;
  rule: string;
  status: 'done' | 'cur' | 'next';
}

export const AGENDA: AgendaT[] = [
  { round: 0, label: '开场陈述',  rule: 'round-robin · 每人 1 次',   status: 'done' },
  { round: 1, label: '反方质询',  rule: 'stance-pair · 反方先开火',  status: 'done' },
  { round: 2, label: '修正与回应', rule: 'reactive · @提及优先',      status: 'cur'  },
  { round: 3, label: '风险评估',  rule: 'moderated · 主持人点名',    status: 'next' },
  { round: 4, label: '投票',      rule: 'vote · 阈值 4/6',          status: 'next' },
  { round: 5, label: '闭幕陈词',  rule: '评委收束',                  status: 'next' },
];

export function agentById(id: string): AgentT | undefined {
  return AGENTS.find(a => a.id === id);
}

/** Build the prompt context that an agent "saw" before generating msg. */
export function buildContextFor(msg: MessageT, transcript: MessageT[]) {
  const agent = agentById(msg.agentId);
  const round = AGENDA[msg.round];
  const prior = transcript.filter(m => {
    if (m.id === msg.id) return false;
    if (m.round < msg.round) return true;
    if (m.round === msg.round) {
      const idxThis = transcript.findIndex(x => x.id === msg.id);
      const idxOther = transcript.findIndex(x => x.id === m.id);
      return idxOther < idxThis;
    }
    return false;
  });

  const windowSize = 20;
  const visible = prior.slice(-windowSize);
  const summarized = prior.slice(0, Math.max(0, prior.length - windowSize));

  const instructions = [
    '请在 80 字内陈述你对议题的初始立场, 给出 1 个核心论据。',
    '反方先发言, 攻击 r1 的最薄弱论点, 必须给出具体数字反驳。',
    '回应 @你被提及的内容, 可以让步、可以坚持。引用上一轮原话。',
    '只发表一个最关键的风险点, 不允许重复别人说过的。',
    '只回 approve / approve_with_conditions / block / abstain, 一句话理由。',
    '基于全部讨论给出最终判词, 200 字以内。',
  ];

  const motion = '是否在 Q3 进入巴西市场, 烧 ≤ $1.2M 做 9 个月 pilot?';

  return {
    agent,
    round,
    motion,
    systemPrompt: agent ? (agent.persona || '') : '',
    knowledge: agent && agent.id === 'A2' ? [
      'q2-financials.pdf',
      'unit-economics-model.xlsx',
      'cfo-historical-decisions.md',
    ] : [],
    instruction: instructions[msg.round] || '',
    rollingSummary: summarized.length > 0
      ? `[r0–r${Math.max(0, msg.round - 1)} 摘要 · ${summarized.length} 条] 已讨论: ${summarized.map(m => agentById(m.agentId)?.short).join(' → ')} 。CEO 主张快进 SP 试点;CFO 反对当前 unit-economics;CRO 提出 LGPD 合规风险;反方陈词聚焦时机/渠道/注意力三错位。`
      : null,
    visibleHistory: visible.map(m => ({
      id: m.id,
      agent: agentById(m.agentId),
      round: m.round,
      ts: m.ts,
      text: (m.chunks || []).join(''),
      isUser: m.isUser || m.agentId === 'U1',
      role: m.role,
    })),
    tokensIn: msg.tokensIn,
    tokensOut: msg.tokensOut,
    costCents: msg.costCents,
    model: agent && agent.model ? MODELS[agent.model] : null,
  };
}

/* -------------------------------------------------------------------------- */
/* Starter packs — pre-built agent rosters + agendas a user can spin up fast. */
/* -------------------------------------------------------------------------- */

export interface AgentSeed {
  name: string;
  persona: string;
  stance: Stance;
  stanceLabel: string;
  short: string;
  modelId: ModelId;
}

export interface RoundSeed {
  label: string;
  rule: string;
  instruction: string;
}

export interface AgendaSeed {
  rounds: RoundSeed[];
}

export interface StarterPack {
  id: string;
  name: string;
  description: string;
  meetingType: 'debate' | 'decision' | 'consult';
  agents: AgentSeed[];
  agenda: AgendaSeed;
}

export const STARTER_PACKS: StarterPack[] = [
  {
    id: 'executive-decision',
    name: '高管决策会',
    description: '6 位高管角色就一项重大商业决策展开多轮辩论, 最终投票收束。',
    meetingType: 'decision',
    agents: [
      { name: 'Aria · CEO',      short: 'AC', stance: 'pro',     stanceLabel: '推进派',   modelId: 'claude-sonnet',     persona: '战略乐观、增长导向,看长期上行。' },
      { name: 'Bran · CFO',      short: 'BC', stance: 'finance', stanceLabel: '财务派',   modelId: 'openai-gpt4o',      persona: '看现金流、单位经济、回收期。' },
      { name: 'Cyra · 风险官',   short: 'CR', stance: 'risk',    stanceLabel: '风险派',   modelId: 'gemini-pro',        persona: '识别黑天鹅与监管风险。' },
      { name: 'Doru · 反方',     short: 'DR', stance: 'con',     stanceLabel: '反对派',   modelId: 'claude-sonnet',     persona: '红队角色,寻找方案漏洞。' },
      { name: 'Echo · 用户代表', short: 'EU', stance: 'neutral', stanceLabel: '用户视角', modelId: 'openai-gpt4o-mini', persona: '从普通消费者体验角度评估。' },
      { name: 'Faye · 评委',     short: 'FJ', stance: 'judge',   stanceLabel: '评委',     modelId: 'claude-sonnet',     persona: '裁定胜负,只在 closing 发言。' },
    ],
    agenda: {
      rounds: [
        { label: '开场陈述',   rule: 'round-robin · 每人 1 次',  instruction: '请在 80 字内陈述你对议题的初始立场, 给出 1 个核心论据。' },
        { label: '反方质询',   rule: 'stance-pair · 反方先开火', instruction: '反方先发言, 攻击 r1 的最薄弱论点, 必须给出具体数字反驳。' },
        { label: '修正与回应', rule: 'reactive · @提及优先',     instruction: '回应 @你被提及的内容, 可以让步、可以坚持。引用上一轮原话。' },
        { label: '风险评估',   rule: 'moderated · 主持人点名',   instruction: '只发表一个最关键的风险点, 不允许重复别人说过的。' },
        { label: '投票',       rule: 'vote · 阈值 4/6',         instruction: '只回 approve / approve_with_conditions / block / abstain, 一句话理由。' },
        { label: '闭幕陈词',   rule: '评委收束',                 instruction: '基于全部讨论给出最终判词, 200 字以内。' },
      ],
    },
  },
  {
    id: 'red-blue-team',
    name: '红蓝对抗',
    description: '红队攻击者 vs 蓝队防御者, 评委裁定, 观察员旁记。适合做安全、产品红队演练。',
    meetingType: 'debate',
    agents: [
      { name: 'Rex · 红队',    short: 'RX', stance: 'con',     stanceLabel: '攻击方', modelId: 'deepseek-reasoner', persona: '红队攻击者,寻找漏洞、滥用路径与最坏情况。' },
      { name: 'Blu · 蓝队',    short: 'BL', stance: 'pro',     stanceLabel: '防御方', modelId: 'claude-opus',       persona: '蓝队防御者,设计缓解措施与纵深防御。' },
      { name: 'Jade · 评委',   short: 'JD', stance: 'judge',   stanceLabel: '评委',   modelId: 'claude-sonnet',     persona: '裁定每轮交锋的胜负与核心威胁等级。' },
      { name: 'Ozzy · 观察员', short: 'OZ', stance: 'neutral', stanceLabel: '观察员', modelId: 'openai-gpt4o-mini', persona: '只记录、不下场,负责事实校对。' },
    ],
    agenda: {
      rounds: [
        { label: '开场',   rule: 'round-robin · 红蓝各开一炮',   instruction: '红队先抛出最尖锐的攻击假设, 蓝队简述当前防御姿态。' },
        { label: '探测',   rule: 'red-team-leads · 蓝队回应',    instruction: '红队列出 3 条具体攻击向量, 蓝队就每条给出现有缓解或缺口。' },
        { label: '反驳',   rule: 'blue-team-leads · 红队回应',   instruction: '蓝队提出补强方案, 红队尝试绕过或给出剩余风险。' },
        { label: '裁决',   rule: 'judge-only · 观察员收尾',      instruction: '评委给出威胁等级与防御评分, 观察员补充事实纠偏。' },
      ],
    },
  },
  {
    id: 'socratic-tutor',
    name: '苏格拉底式辅导',
    description: '导师不直接给答案,通过提问引导学生自己推导;观察员记录学习路径。',
    meetingType: 'consult',
    agents: [
      { name: 'Sage · 导师',   short: 'SG', stance: 'host',    stanceLabel: '导师',     modelId: 'claude-opus',       persona: '苏格拉底式导师,只通过提问引导,不直接给答案。' },
      { name: 'Pip · 学生',    short: 'PP', stance: 'neutral', stanceLabel: '学生',     modelId: 'openai-gpt4o-mini', persona: '好奇但容易跳步, 需要被引导回到第一性原理。' },
      { name: 'Lin · 观察员',  short: 'LN', stance: 'judge',   stanceLabel: '观察员',   modelId: 'gemini-flash',      persona: '记录学生的认知路径与卡点, 在最后给出反馈。' },
    ],
    agenda: {
      rounds: [
        { label: '提出问题',   rule: 'tutor-opens',           instruction: '导师抛出一个开放性问题, 不给答案, 只设定边界。' },
        { label: '探究对话',   rule: 'tutor-questions · 学生作答', instruction: '导师持续提问, 学生作答; 学生每回答一次就反思一次假设。' },
        { label: '总结复盘',   rule: 'observer-closes',       instruction: '观察员复盘学生卡在哪些步骤, 给出可迁移的思维提示。' },
      ],
    },
  },
];
